import React, {useState, useEffect, useRef} from "react"
// MUI
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
// Generator
import QRCodeGenerator from "@/components/QRCodeGenerator/QRCodeGenerator";
// Data
import {useData} from "@/context/PackagingDataProvider";
// Assets
// @ts-ignore
import QRCodePlaceholder from "@/assets/png/qrcode-preview.png";

export default function QRCodeGeneratorDialog(props: any) {

    const {open, onClose, product, setProduct, usedModule, verifiedGtin, t} = props

    const {createQRCode, getWIPQRCodePreview, domains, getDomains, qrcodeTemplates, getTemplates} = useData()
    const shortUrlPrefix = "https://opn.to"

    const [qrcode, setQRCode] = useState<any>({
        settings: {
            frame: null,
            template_id: '',
            redundancy: "M",
            eyes: {
                type: "leaf",
                color_ext_top_left: '',
                color_ext_top_right: '',
                color_ext_bottom_left: '',
                color_int_bottom_left: '',
                color_int_top_right: '',
                color_int_top_left: ''
            },
            layout: {
                gradient_type: "horizontal",
                type: "single_color",
                color_background: "#ffffff",
                color_one: "#000000",
                color_two: "#afc928",
                color_shadow: "#bdbdbd",
                force_shadow: "" // L, M, S
            },
            logo: {asset_uuid: '', excavate: false, url: '', width: 0, height: 0, x: 0, x_norm: 0, y: 0, y_norm: 0},
            background: {url: '', contrast: 0.0, brightness: 0.0},
            modules: {type: "simple"}
        },
        data: {
            type: "URL",
            resolution: "dynamic",
            url: "",
            label: "",
            preferred_short_url: ""
        }
    })
    const [preview, setPreview] = useState<any>(QRCodePlaceholder)
    const [previewWarning, setPreviewWarning] = useState<boolean>(false)
    // QR Code data management
    // True/False for UI management - undefined for loading
    const [isDataSubmitted, setIsDataSubmitted] = useState<boolean | undefined>(false)
    // QR Code creation in-flight guard - prevents duplicate submissions.
    // `creating` drives the button UI (spinner + disabled); `creatingRef` is a
    // synchronous guard so two clicks in the same tick (before React re-renders
    // and disables the button) cannot both fire a creation request.
    const [creating, setCreating] = useState<boolean>(false)
    const creatingRef = useRef<boolean>(false)
    // Logo data
    const [logo, setLogo] = useState<any>({
        replacing: false,
        position: "auto",
        x: 190,
        y: 190,
        coordX: 0,
        coordY: 0,
        startX: 190,
        startY: 190,
        width: 120,
        height: 120,
        file: null
    })

    // check for domains on open
    useEffect(() => {
        if (open) {
            setCreating(false)
            creatingRef.current = false
            getDomains().then()
            getTemplates().then()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // if domains, switch to Dyna-Pro
    useEffect(() => {
        if (!!domains) {

            let activeDomains = domains.filter((dom: any) => { return dom.activated })

            setQRCode({
                ...qrcode,
                data: {
                    ...qrcode.data,
                    resolution: "dynamic-pro",
                    domain_id: (!!activeDomains && !!activeDomains[0]) ? activeDomains[0].domain_id : "",
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domains]);

    // Auto setup name and/or data url when entering the dialog
    // Remove to allow QR Codes types integration
    useEffect(() => {
        if (open && product) {
            if (usedModule === "GS1") {
                setQRCode({
                    ...qrcode, data: {
                        ...qrcode.data,
                        label: product.name,
                        url: product.default_url,
                        short_url: !!verifiedGtin ? verifiedGtin : ""
                }})
            } else {
                setQRCode({
                    ...qrcode, data: {
                        ...qrcode.data,
                        type: "packaging",
                        label: product.name,
                        url: product.default_url
                }})
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, product, usedModule])

    // Save
    const handleSaveQRCode = async () => {
        // Guard against duplicate submissions while a creation is in-flight
        if (creatingRef.current) return
        creatingRef.current = true
        setCreating(true)

        try {
            const type = usedModule === "GS1" ? "serial" : "geolocation"
            const res: any = await createQRCode(qrcode, type)
            if (res) {
                setProduct({...product, qrcode: res.qrcode_id})
                onClose()
            } else {
                // Creation failed - re-enable the button so the user can retry
                creatingRef.current = false
                setCreating(false)
            }
        } catch (e) {
            creatingRef.current = false
            setCreating(false)
        }
    }

    // Preview management - previously known as fetchPReview()
    const handleSubmitQRCodeData = async () => {
        // Packaging-specific logic
        let previewType = verifiedGtin ? "serial" : ""

        await getWIPQRCodePreview(qrcode, previewType).then((res: any) => {
            if (!!res) {
                setPreview(res.preview)

                if (res.header !== null && res.header === "CONTRAST_WARNING") setPreviewWarning(true)
                else setPreviewWarning(false)

                if (!isDataSubmitted) setIsDataSubmitted(true)
            } else setIsDataSubmitted(undefined)
        })

    }

    return (
        <>
            <Dialog open={open} maxWidth={usedModule === "GS1" ? "md" : "xl"} fullWidth>
                <DialogTitle>{t("products:qrcode_generator")}</DialogTitle>
                <DialogContent sx={{ pb: 4 }}>
                    <QRCodeGenerator
                        onCreate={() => handleSaveQRCode()}
                        onSubmit={() => handleSubmitQRCodeData()}
                        onUnsubmit={() => setIsDataSubmitted(false)}
                        submitted={isDataSubmitted}
                        creating={creating}
                        {...{
                            usedModule,
                            // standard qrcode data
                            qrcode, setQRCode,
                            preview, previewWarning,
                            shortUrlPrefix,
                            // logo data
                            logo, setLogo,
                            // dynamic-pro
                            domains, qrcodeTemplates,
                            // misc
                            t
                    }}/>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center"}}>
                    <Button sx={{ color: "gray" }} onClick={() => onClose()} size={"large"}>
                        {t("back_to_product")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}